import React, { useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { useRouter } from "../router";

// Clave para recordar, en este navegador, si el último acceso fue en modo
// mostrador — pensado para un equipo dedicado a punto de venta (una caja
// registradora física): así el selector ya viene marcado la próxima vez que
// alguien inicie sesión en esa misma máquina, sin tener que elegirlo cada vez.
const CLAVE_MODO_INGRESO = "sigepro:modoIngreso";
type ModoIngreso = "admin" | "mostrador";

function leerModoGuardado(): ModoIngreso {
  try {
    return window.localStorage.getItem(CLAVE_MODO_INGRESO) === "mostrador" ? "mostrador" : "admin";
  } catch {
    return "admin";
  }
}

// Mismo mecanismo, para recordar a qué SISTEMA se entró la última vez desde
// este navegador: Sigepro PR (núcleo privado, empresas del sector privado,
// Fases 1-13) o SigeproEP (entidades públicas — alcaldías/gobernaciones,
// Fase EP). Es el mismo backend y el mismo login; lo único que cambia es a
// qué ruta del front se navega después de autenticarse. No reemplaza los
// permisos del backend (un usuario sigue viendo solo los datos de su propia
// empresa vía `empresa_id` del token) — es solo un atajo de navegación para
// no tener que teclear "/sigepro-ep" a mano.
const CLAVE_SISTEMA = "sigepro:sistema";
type Sistema = "pr" | "ep";

function leerSistemaGuardado(): Sistema {
  try {
    return window.localStorage.getItem(CLAVE_SISTEMA) === "ep" ? "ep" : "pr";
  } catch {
    return "pr";
  }
}

export function Login() {
  const { login, error, requiere2fa } = useAuth();
  const { navigate } = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [modo, setModo] = useState<ModoIngreso>(leerModoGuardado);
  const [sistema, setSistema] = useState<Sistema>(leerSistemaGuardado);

  const elegirModo = (m: ModoIngreso) => {
    setModo(m);
    try { window.localStorage.setItem(CLAVE_MODO_INGRESO, m); } catch { /* almacenamiento no disponible */ }
  };

  const elegirSistema = (s: Sistema) => {
    setSistema(s);
    try { window.localStorage.setItem(CLAVE_SISTEMA, s); } catch { /* almacenamiento no disponible */ }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setEnviando(true);
    try {
      const completo = await login(email, password, requiere2fa ? totp : undefined);
      if (completo && sistema === "ep") {
        // Se eligió "SigeproEP": entra directo al módulo de entidades
        // públicas en vez del Dashboard del núcleo privado.
        navigate("/sigepro-ep");
      } else if (completo && modo === "mostrador") {
        // Se eligió "Punto de venta" antes de loguearse: en vez del Dashboard,
        // se entra directo al mostrador (mismo botón "Ir al mostrador →" que
        // sigue disponible en Pos.tsx para pruebas desde el back office).
        navigate("/pos/mostrador");
      } else if (completo) {
        // Sigepro PR / acceso admin normal: forzar la navegación al Dashboard
        // del núcleo privado. Sin esto, el router (hash-based, no se resetea
        // solo) se queda apuntando a la última ruta visitada — si el usuario
        // venía de una sesión en SigeproEP (ej. "/sigepro-ep"), esa pantalla
        // seguía mostrándose tras entrar en modo PR (bug real reportado por
        // el usuario: "al cambiar de EP a PR se mantiene la página de EP").
        navigate("/");
      }
    } catch {
      // el error ya queda expuesto vía `error` del contexto
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-navy via-navy to-navy-light px-4 relative overflow-hidden">
      <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-accent/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />
      <div className="w-full max-w-sm relative">
        <div className="text-center mb-7">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-accent shadow-popover flex items-center justify-center font-display font-extrabold text-xl text-white mb-4">
            SG
          </div>
          <div className="font-display text-2xl font-bold text-white tracking-tight">SIGEPRO ERP</div>
          <div className="text-xs text-slate-300/90 mt-1">OPCC Consultores · SIG-ARQ-001</div>
        </div>
        <form onSubmit={onSubmit} className="card p-6 space-y-4 shadow-popover border-white/10">
          {!requiere2fa && (
            <>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Sistema</label>
                <div className="grid grid-cols-2 gap-1.5 rounded-lg bg-surface p-1">
                  <button
                    type="button"
                    onClick={() => elegirSistema("pr")}
                    className={`rounded-md py-2 text-xs font-semibold transition-colors ${sistema === "pr" ? "bg-white text-accent-dark shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                  >
                    Sigepro PR
                  </button>
                  <button
                    type="button"
                    onClick={() => elegirSistema("ep")}
                    className={`rounded-md py-2 text-xs font-semibold transition-colors ${sistema === "ep" ? "bg-white text-accent-dark shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                  >
                    SigeproEP
                  </button>
                </div>
                <p className="text-[11px] text-slate-400 mt-1.5">
                  {sistema === "ep"
                    ? "Núcleo para entidades públicas (alcaldías/gobernaciones): presupuesto, contabilidad e informes regulatorios del sector público."
                    : "Núcleo privado: el ERP completo (Fases 1-13) para empresas del sector privado."}
                </p>
              </div>
              {sistema === "pr" && (
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Tipo de acceso</label>
                  <div className="grid grid-cols-2 gap-1.5 rounded-lg bg-surface p-1">
                    <button
                      type="button"
                      onClick={() => elegirModo("admin")}
                      className={`rounded-md py-2 text-xs font-semibold transition-colors ${modo === "admin" ? "bg-white text-accent-dark shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                    >
                      Acceso administrativo
                    </button>
                    <button
                      type="button"
                      onClick={() => elegirModo("mostrador")}
                      className={`rounded-md py-2 text-xs font-semibold transition-colors ${modo === "mostrador" ? "bg-white text-accent-dark shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                    >
                      Punto de venta (mostrador)
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1.5">
                    {modo === "mostrador"
                      ? "Al iniciar sesión se abrirá directamente la terminal de mostrador con las credenciales de este usuario."
                      : "Al iniciar sesión se abrirá el back office administrativo."}
                  </p>
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Correo electrónico</label>
                <input
                  className="input-field"
                  type="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@sigepro.demo"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Contraseña</label>
                <input
                  className="input-field"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>
            </>
          )}

          {requiere2fa && (
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Código de doble factor (TOTP)</label>
              <input
                className="input-field text-center tracking-[0.3em] text-lg"
                inputMode="numeric"
                maxLength={6}
                autoFocus
                value={totp}
                onChange={(e) => setTotp(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                required
              />
              <p className="text-xs text-textmuted mt-1.5">
                Ingrese el código de 6 dígitos de su aplicación autenticadora.
              </p>
            </div>
          )}

          {(error || localError) && (
            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {error || localError}
            </div>
          )}

          <button type="submit" className="btn-primary w-full" disabled={enviando}>
            {enviando ? "Verificando..." : requiere2fa ? "Verificar código" : "Iniciar sesión"}
          </button>
        </form>
        <p className="text-center text-[11px] text-slate-400 mt-4">
          Fase 1 · Core — Configuración, usuarios, roles y auditoría
        </p>
        {/* Antes solo era un texto gris de 11px, casi invisible, así que en
            la práctica no había forma visible de llegar a este panel salvo
            escribiendo /#/superadmin a mano en la URL (pedido real del
            usuario mientras probamos: "no sé cómo entrar a mi superusuario
            ... coloca un botón que me lleve a ingresar mis credenciales").
            Es un botón aparte del login normal porque el superadmin usa su
            propia sesión y sus propias credenciales (panel de licenciamiento
            de módulos, no el ERP de una empresa) — ver Superadmin.tsx. */}
        <button
          type="button"
          onClick={() => navigate("/superadmin")}
          className="w-full flex items-center justify-center gap-1.5 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 hover:border-white/25 transition-colors mt-3 py-2 text-xs font-medium text-slate-300 hover:text-white"
        >
          Panel de superadministrador →
        </button>
      </div>
    </div>
  );
}
